import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {PartnerDgSearchResults} from "../models/datagrid-search-results/partner-dg-search.results";
import {Partner} from "../models/partner.model";

/**
 * Serwis warstwy danych zarządzający podmiotami partnerskimi w systemie.
 * Obsługuje operacje CRUD dla kont Partnerów, zasilanie tabel DataGrid,
 * audytowanie zmian oraz integrację Partnerów z modułem E-commerce (Sklepy).
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerService implements DataGridServiceInterface<Observable<PartnerDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę partnerów w celu zasilenia widoku tabelarycznego (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie względem początku listy wyników.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga określająca widoczność partnerów zarchiwizowanych/usuniętych.
   * @returns Observable emitujący ustandaryzowany obiekt wyników wyszukiwania DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partners/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Partner(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje partnerów w systemie na podstawie zdefiniowanych filtrów (wyszukiwanie zaawansowane).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników.
   * @param searchFieldsData Mapa (Słownik) z kryteriami wyszukiwania (nazwa pola -> szukana wartość).
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności zarchiwizowanych rekordów.
   * @returns Observable emitujący przefiltrowany obiekt wyników dla tabeli.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/partners/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new Partner(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje zmiany w profilu istniejącego partnera w bazie danych (Aktualizacja).
   *
   * @param partnerEdited Zmodyfikowany obiekt partnera.
   * @returns Observable emitujący zaktualizowany obiekt partnera z serwera.
   */
  updatePartner(partnerEdited: Partner): Observable<Partner> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/partners', partnerEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Rejestruje nowego partnera w systemie.
   *
   * @param partner Obiekt zawierający dane nowego partnera.
   * @returns Observable emitujący zapisany obiekt partnera wraz z nadanym UUID.
   */
  addPartner(partner: Partner): Observable<Partner> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/partners`, partner).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dziennik audytowy (historię zmian) dla konkretnego profilu partnera.
   *
   * @param uuid Unikalny identyfikator partnera.
   * @returns Observable emitujący tablicę migawek (Snapshot) z historią modyfikacji.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partners/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa profil partnera z systemu na podstawie jego unikalnego identyfikatora.
   *
   * @param partnerUUID Identyfikator partnera do usunięcia.
   * @returns Observable emitujący stan usuniętego obiektu.
   */
  deletePartner(partnerUUID: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/partners/` + partnerUUID).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełną, płaską listę wszystkich aktywnych partnerów (bez paginacji)
   *
   * @returns Observable emitujący tablicę obiektów Partner.
   */
  listPartners(): Observable<Partner[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/partners-all').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Partner(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły pojedynczego partnera w celu załadowania ich do formularza edycji.
   *
   * @param uuid Unikalny identyfikator partnera.
   * @returns Observable emitujący pobrany obiekt profilu partnera.
   */
  getPartnerToEdit(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partners/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje profil partnera powiązanego z konkretnym kontem lekarza weterynarii.
   *
   * @param vetDoctorUUID Identyfikator konta lekarza.
   * @returns Observable emitujący znaleziony obiekt profilu partnerskiego.
   */
  getByVetDoctorUUID(vetDoctorUUID: string): Observable<Partner> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partners/by-doctor/${vetDoctorUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Partner>(new Partner(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wyselekcjonowaną listę partnerów, którzy posiadają w systemie aktywny
   * i skonfigurowany moduł sprzedażowy (Sklep / E-commerce).
   *
   * @returns Observable emitujący tablicę partnerów z aktywnym sklepem.
   */
  getPartnersWithShop(): Observable<Partner[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/partner-shops').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Partner(),[] ,vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
